import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';
import { Risk, RiskRAG } from '../types';

// Probability/Impact picklist is now 5-level (extended Apr 19 Run 3):
// VeryLow=100000003(rank 1), Low=100000000(rank 2), Medium=100000001(rank 3),
// High=100000002(rank 4), VeryHigh=100000004(rank 5). Both axes use the
// same picklist option set, so the same rank map applies.
const PROB_RANK: Record<number, number> = {
  100000003: 1, // VeryLow
  100000000: 2, // Low
  100000001: 3, // Medium
  100000002: 4, // High
  100000004: 5, // VeryHigh
};
export function calcRiskScore(probability: number, impact: number): number {
  const p = PROB_RANK[probability] ?? 0;
  const i = PROB_RANK[impact] ?? 0;
  return p * i;
}

// Normalised score 0.0–1.0 on the 5x5 matrix. Min product 1x1 = 1/25 = 0.04,
// max product 5x5 = 25/25 = 1.0. Replaces the previous 3x3 divisor of 9.
export function calcNormalisedRiskScore(probability: number, impact: number): number {
  const raw = calcRiskScore(probability, impact);
  return Math.round((raw / 25) * 100) / 100;
}

// RAG thresholds re-tuned for the 5x5 matrix so the diagonal band reads as
// amber. Corners and near-corners become clearly green or red.
//   VL/VL = 1/25 = 0.04 (Green)
//   VL/M, M/VL = 3/25 = 0.12 (Green)
//   L/M, M/L = 6/25 = 0.24 (Amber)
//   M/M = 9/25 = 0.36 (Amber)
//   H/M = 12/25 = 0.48 (Amber)
//   H/H = 16/25 = 0.64 (Red)
//   VH/VH = 25/25 = 1.0 (Red)
export function deriveRag(normalisedScore: number): number {
  if (normalisedScore <= 0.15) return RiskRAG.Green;
  if (normalisedScore <= 0.50) return RiskRAG.Amber;
  return RiskRAG.Red;
}

const FIELDS = [
  'dagrun_pm_riskid','dagrun_pm_name','dagrun_pm_description','dagrun_pm_probability',
  'dagrun_pm_impact','dagrun_pm_priorityscore',
  'dagrun_pm_riskscore','dagrun_pm_rag',
  'dagrun_pm_status','dagrun_pm_ownerid','dagrun_pm_ownername',
  'dagrun_pm_mitigationplan','dagrun_pm_contingencyplan','dagrun_pm_clientvisible',
  '_dagrun_pm_projectid_value',
];

export async function getProjectRisks(pca: IPublicClientApplication, projectId: string): Promise<Risk[]> {
  return dvList<Risk>(pca, 'dagrun_pm_risks', {
    select: FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    orderby: 'dagrun_pm_priorityscore desc',
  });
}

export async function createRisk(pca: IPublicClientApplication, projectId: string, name: string): Promise<string> {
  const prob = 100000001, imp = 100000001;
  const normalised = calcNormalisedRiskScore(prob, imp);
  return dvCreate(pca, 'dagrun_pm_risks', {
    dagrun_pm_name: name,
    dagrun_pm_probability: prob,
    dagrun_pm_impact: imp,
    dagrun_pm_riskscore: normalised,
    dagrun_pm_rag: deriveRag(normalised),
    dagrun_pm_status: 100000000,
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  });
}

// Patch a risk's fields, automatically recomputing the new scoring fields
// (dagrun_pm_riskscore + dagrun_pm_rag) whenever probability or impact
// changes in the patch.
export async function updateRisk(pca: IPublicClientApplication, id: string, patch: Record<string, unknown>): Promise<void> {
  if (patch.dagrun_pm_probability != null || patch.dagrun_pm_impact != null) {
    // Need the other field's current value to recompute. If only one of the
    // two is in the patch, fetch the risk first.
    let prob = patch.dagrun_pm_probability as number | undefined;
    let imp  = patch.dagrun_pm_impact as number | undefined;
    if (prob == null || imp == null) {
      try {
        const current = await dvList<Risk>(pca, 'dagrun_pm_risks', {
          select: ['dagrun_pm_probability', 'dagrun_pm_impact'],
          filter: `dagrun_pm_riskid eq ${id}`,
          top: 1,
        });
        if (current[0]) {
          if (prob == null) prob = current[0].dagrun_pm_probability;
          if (imp  == null) imp  = current[0].dagrun_pm_impact;
        }
      } catch { /* best effort */ }
    }
    if (prob != null && imp != null) {
      const normalised = calcNormalisedRiskScore(prob, imp);
      (patch as Record<string, unknown>).dagrun_pm_riskscore = normalised;
      (patch as Record<string, unknown>).dagrun_pm_rag       = deriveRag(normalised);
      (patch as Record<string, unknown>).dagrun_pm_priorityscore = calcRiskScore(prob, imp);
    }
  }
  return dvUpdate(pca, 'dagrun_pm_risks', id, patch);
}

export async function deleteRisk(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_risks', id);
}

export async function createRiskWithFields(
  pca: IPublicClientApplication,
  projectId: string,
  fields: {
    name: string;
    description?: string | null;
    probability?: number;
    impact?: number;
    ownerid?: string | null;
    ownername?: string | null;
    mitigationplan?: string | null;
    contingencyplan?: string | null;
  },
): Promise<string> {
  const probability = fields.probability ?? 100000001;
  const impact      = fields.impact ?? 100000001;
  const normalised  = calcNormalisedRiskScore(probability, impact);
  const body: Record<string, unknown> = {
    dagrun_pm_name: fields.name,
    dagrun_pm_probability: probability,
    dagrun_pm_impact: impact,
    dagrun_pm_priorityscore: calcRiskScore(probability, impact),
    dagrun_pm_riskscore: normalised,
    dagrun_pm_rag: deriveRag(normalised),
    dagrun_pm_status: 100000000, // Identified
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  };
  if (fields.description)     body.dagrun_pm_description = fields.description;
  if (fields.ownerid)         body.dagrun_pm_ownerid = fields.ownerid;
  if (fields.ownername)       body.dagrun_pm_ownername = fields.ownername;
  if (fields.mitigationplan)  body.dagrun_pm_mitigationplan = fields.mitigationplan;
  if (fields.contingencyplan) body.dagrun_pm_contingencyplan = fields.contingencyplan;
  return dvCreate(pca, 'dagrun_pm_risks', body);
}
